import {
  Registration,
  Merchant,
  RegistrationFieldPermissionsField,
  Brand,
} from '@stax-core/types-omni';
import { Mutator } from 'final-form';
import {
  BusinessFieldsValues,
  BusinessFieldsErrors,
} from './BusinessFields.types';
import { pick, isEmpty } from '../../util/object.util';
import {
  isAddressPoBox,
  isAddressApo,
  isMeaningfulString,
  isValidPhoneNumber,
  isValidUrl,
  isValidEmail,
} from '../../util/validator.util';
import { getCombinedFieldsVisibilityLevels } from '../../hooks';

export function mapFormValuesToPayload(
  formValues: BusinessFieldsValues,
  registration?: Registration | null | undefined,
): Partial<Registration> {
  const { hasSameBillingAndLocationAddress, ...payload } = formValues;

  if (payload.business_phone_number) {
    payload.business_phone_number = payload.business_phone_number
      .replace(/[\D]/g, '')
      .slice(0, 10);
  }

  if (payload.business_location_phone_number) {
    payload.business_location_phone_number =
      payload.business_location_phone_number.replace(/[\D]/g, '').slice(0, 10);
  }

  if (formValues.hasSameBillingAndLocationAddress) {
    payload.business_address_1 = payload.business_location_address_1 || '';
    payload.business_address_2 = payload.business_location_address_2 || '';
    payload.business_address_city =
      payload.business_location_address_city || '';
    payload.business_address_state =
      payload.business_location_address_state || '';
    payload.business_address_zip = payload.business_location_address_zip || '';
    payload.business_address_country =
      payload.business_location_address_country || '';
    payload.business_phone_number =
      payload.business_location_phone_number || '';
  }

  // BlockChyp boarding reads business_phone_number, which is optional here
  if (
    !payload.business_phone_number &&
    payload.business_location_phone_number
  ) {
    payload.business_phone_number = payload.business_location_phone_number;
  }

  if (payload.business_fax) {
    payload.business_fax = payload.business_fax
      .replace(/[\D]/g, '')
      .slice(0, 10);
  }

  if (payload.business_tax_id) {
    payload.business_tax_id = payload.business_tax_id
      .replace(/[\D]/g, '')
      .slice(0, 9);
  }

  if (!payload.business_address_1) {
    payload.business_address_1 = null;
  }
  if (!payload.business_location_address_1) {
    payload.business_location_address_1 = null;
  }
  if (!payload.business_address_2) {
    payload.business_address_2 = null;
  }
  if (!payload.business_location_address_2) {
    payload.business_location_address_2 = null;
  }

  // This is a special case
  // If the user picks Sole Proprietor, we need to delete all of the existing representative's data
  if (payload.company_type === 'S' && registration) {
    payload.meta = {
      ...registration?.meta,
      ownership_percentage: '100',
      owner_citizenship: registration.meta?.owner_citizenship ?? 'USA',
      representatives: [],
    };
  }
  return payload;
}

export function mapRegistrationToInitialFormValues(
  registration: Registration | null | undefined,
): BusinessFieldsValues {
  if (!registration) return {} as BusinessFieldsValues;

  const initialValues = pick(
    registration,
    'business_legal_name',
    'business_dba',
    'business_website',
    'business_email',
    'business_fax',
    'business_phone_number',
    'business_location_phone_number',
    'business_address_1',
    'business_location_address_1',
    'business_address_2',
    'business_location_address_2',
    'business_address_city',
    'business_location_address_city',
    'business_address_state',
    'business_location_address_state',
    'business_address_zip',
    'business_location_address_zip',
    'business_address_country',
    'business_location_address_country',
    'business_tax_id',
    'business_open_date',
    'company_type',
  ) as BusinessFieldsValues;

  if (initialValues) {
    if (!initialValues.business_email && registration.email) {
      initialValues.business_email = registration.email;
    }
    initialValues.hasSameBillingAndLocationAddress =
      hasSameBillingAndLocationAddress(initialValues);
  }

  return initialValues;
}

export function mapFormValuesToTeamPayload(
  formValues: BusinessFieldsValues,
): Partial<Merchant> {
  return {
    // prettier-ignore
    address_1: formValues.business_location_address_1 || '',
    address_2: formValues.business_location_address_2 || '',
    address_city: formValues.business_location_address_city || '',
    address_state: formValues.business_location_address_state || '',
    address_zip: formValues.business_location_address_zip || '',
    address_country: formValues.business_location_address_country || '',
  };
}

export function validateRequiredFormValues(
  values: BusinessFieldsValues,
): BusinessFieldsErrors {
  const errors = {} as BusinessFieldsErrors;

  let requiredFields = [
    'business_legal_name',
    'business_dba',
    'business_email',
    'business_location_phone_number',
    'business_tax_id',
    'business_open_date',
    'company_type',
    'business_location_address_1',
    'business_location_address_city',
    'business_location_address_state',
    'business_location_address_zip',
    'business_location_address_country',
  ];
  if (!values.hasSameBillingAndLocationAddress) {
    requiredFields.push(
      'business_address_1',
      'business_address_city',
      'business_address_state',
      'business_address_zip',
      'business_address_country',
    );
  }

  // Don't require state if country isn't USA
  if (!['USA', 'CAN'].includes(values.business_address_country!)) {
    requiredFields = requiredFields.filter(
      field => field !== 'business_address_state',
    );
  }

  if (!['USA', 'CAN'].includes(values.business_location_address_country!)) {
    requiredFields = requiredFields.filter(
      field => field !== 'business_location_address_state',
    );
  }

  requiredFields.forEach(field => {
    if (!values[field]) {
      errors[field] = 'Required';
    }
  });

  return errors;
}

export function validateFormValues(
  values: BusinessFieldsValues,
): BusinessFieldsErrors {
  const errors = {} as BusinessFieldsErrors;

  if (
    values.business_location_address_1 &&
    isAddressPoBox(values.business_location_address_1)
  ) {
    errors.business_location_address_1 = 'Address cannot be a PO Box';
  }

  if (
    values.business_location_address_2 &&
    isAddressPoBox(values.business_location_address_2)
  ) {
    errors.business_location_address_2 = 'Address cannot be a PO Box';
  }

  if (
    values.business_location_address_1 &&
    isAddressApo(values.business_location_address_1)
  ) {
    errors.business_location_address_1 = 'Address cannot be an APO/FPO/DPO';
  }

  if (
    values.business_location_address_2 &&
    isAddressApo(values.business_location_address_2)
  ) {
    errors.business_location_address_2 = 'Address cannot be an APO/FPO/DPO';
  }

  if (values.business_fax && !isValidPhoneNumber(values.business_fax)) {
    errors.business_fax = 'Please enter a valid fax number';
  }

  if (values.business_website && !isValidUrl(values.business_website)) {
    errors.business_website = 'Please enter a valid website';
  }

  if (values.business_email && !isValidEmail(values.business_email)) {
    errors.business_email = 'Please enter a valid email';
  }

  if (
    values.business_phone_number &&
    !isValidPhoneNumber(values.business_phone_number)
  ) {
    errors.business_phone_number = 'Please enter a valid business phone number';
  }

  if (
    values.business_location_phone_number &&
    !isValidPhoneNumber(values.business_location_phone_number)
  ) {
    errors.business_location_phone_number =
      'Please enter a valid business phone number';
  }

  if (
    values.business_tax_id &&
    (/[^0-9\-]/.test(values.business_tax_id) ||
      values.business_tax_id.replace(/[^0-9]/g, '').length !== 9)
  ) {
    errors.business_tax_id =
      'Invalid tax ID. Please enter your 9 digit tax ID number. Under some circumstances an applicant may use their SSN instead.';
  }

  if (
    values.business_legal_name &&
    !isMeaningfulString(values.business_legal_name)
  ) {
    errors.business_legal_name = 'Please enter a valid business legal name';
  }

  if (values.business_dba && !isMeaningfulString(values.business_dba)) {
    errors.business_dba = 'Please enter a valid business dba';
  }

  return errors;
}

export function hasSameBillingAndLocationAddress(
  formValues: BusinessFieldsValues,
) {
  if (isEmpty(formValues)) return true;

  // prettier-ignore
  return (
    (formValues.business_address_1 || '') === (formValues.business_location_address_1 || '') &&
    (formValues.business_address_2 || '') === (formValues.business_location_address_2 || '') &&
    (formValues.business_address_city || '') === (formValues.business_location_address_city || '') &&
    (formValues.business_address_state || '') === (formValues.business_location_address_state || '') &&
    (formValues.business_address_zip || '') === (formValues.business_location_address_zip || '') &&
    (formValues.business_address_country || '') === (formValues.business_location_address_country || '') &&
    (formValues.business_location_phone_number || '') === (formValues.business_phone_number || '')
  ) || (
      !formValues.business_address_1 &&
      !formValues.business_address_2 &&
      !formValues.business_address_city &&
      !formValues.business_address_state &&
      !formValues.business_address_zip &&
      !formValues.business_address_country &&
      !formValues.business_location_phone_number
    )
}

export const formMutators: { [key: string]: Mutator<BusinessFieldsValues> } = {
  setFieldValue: ([name, value], state, utils) => {
    utils.changeValue(state, name, _ => value);
  },
};
export const necessaryBusinessFields: RegistrationFieldPermissionsField[] = [
  // misc business fields
  'business_legal_name',
  'business_dba',
  'business_website',
  'business_location_phone_number',
  'business_fax',
  'business_tax_id',
  'business_open_date',

  'company_type',

  // business location address fields
  'business_location_address_1',
  // NOTE: 'business_location_address_2' field is not necessary
  'business_location_address_city',
  'business_location_address_state',
  'business_location_address_zip',
  'business_location_address_country',

  // NOTE: business_address_* (business billing address) fields are not necessary
];
export function areBusinessFieldsHidden(
  brand: Brand | null,
  registration: Registration | null,
) {
  const { none } = getCombinedFieldsVisibilityLevels(
    registration,
    brand,
    ...necessaryBusinessFields,
  );

  /**
   * IF none of the necessary fields are visible (i.e. they have been prefilled or otherwise turned off)
   * THEN return true (do not show this page)
   */
  return none;
}

export function isBusinessPageHidden(brand: Brand) {
  return !brand.regSidebarItems?.includes('Business');
}
